import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Header.css';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  const closeUserMenu = () => setIsUserMenuOpen(false);

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  const handleProfileClick = () => {
    closeUserMenu();
    navigate('/profile');
  };

  const handleAdminClick = () => {
    closeUserMenu();
    navigate('/admin');
  };

  const handleLogout = async () => {
    closeUserMenu();
    await logout();
    navigate('/login');
  };

  return (
    <header className="header">
      <div className="container header-inner">
        <button
          type="button"
          onClick={handleLogoClick}
          className="header-logo-button"
          aria-label="Перейти на главную"
        >
          <img src="/logo/logo.svg" alt="aivion" className="header-logo" />
        </button>
        <div className="header-user-menu">
          <div className="header-notifications">
            <img
              src="/icons/header/notf.svg"
              alt="Notifications"
              className="header-bell"
            />
            <span className="header-badge">3</span>
          </div>
          {user && (
            <button
              type="button"
              className="header-balance"
              aria-label="Перейти на страницу баланса"
              onClick={() => navigate('/balance')}
            >
              {(user.balance ?? 0).toLocaleString('ru-RU')} ₽
            </button>
          )}
          <div className="header-user-dropdown" ref={userMenuRef}>
            <button
              type="button"
              className="header-user-trigger"
              aria-label="Меню пользователя"
              aria-expanded={isUserMenuOpen}
              aria-haspopup="menu"
              onClick={() => setIsUserMenuOpen((prev) => !prev)}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/header/user.svg`}
                alt=""
                className="header-user-trigger__icon"
                aria-hidden="true"
              />
              <span className="header-user-trigger__name">{user?.name || 'Пользователь'}</span>
              <svg
                className={`header-user-trigger__chevron${isUserMenuOpen ? ' header-user-trigger__chevron--open' : ''}`}
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {isUserMenuOpen && (
              <div className="header-user-menu-dropdown" role="menu">
                <button
                  type="button"
                  className="header-user-menu-dropdown__item"
                  role="menuitem"
                  onClick={handleProfileClick}
                >
                  Профиль
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className="header-user-menu-dropdown__item"
                    role="menuitem"
                    onClick={handleAdminClick}
                  >
                    Админ-панель
                  </button>
                )}
                <button
                  type="button"
                  className="header-user-menu-dropdown__item header-user-menu-dropdown__item--danger"
                  role="menuitem"
                  onClick={() => void handleLogout()}
                >
                  Выйти
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
